/**
 * Analysis chat widgets: TableWidget, ChartWidget, MessageRenderer.
 *
 * MessageRenderer walks an assistant message's segments in order and renders
 * each as the right component. Chart specs may arrive either pre-extracted
 * (segment type='chart_spec') or inline as fenced ```glaium-chart``` blocks
 * inside a markdown segment — the renderer handles both.
 */
import { Fragment, useMemo, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import remarkBreaks from 'remark-breaks'
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts'
import { Save, AlertCircle, ChevronDown, ChevronRight, Wrench } from 'lucide-react'

import { Button } from '@/components/ui/button'
import type { Segment, ChartSpec, AnalysisMessage } from '@/services/analysis'

// ---------------------------------------------------------------------------
// TableWidget
// ---------------------------------------------------------------------------

interface TableWidgetProps {
  rows: Record<string, unknown>[]
  columns: string[]
  truncated?: boolean
  onSaveAsReport?: () => void
}

export function TableWidget({ rows, columns, truncated, onSaveAsReport }: TableWidgetProps) {
  if (!rows.length) {
    return (
      <div className="my-2 rounded border border-dashed p-3 text-xs text-muted-foreground">
        No rows returned.
      </div>
    )
  }
  return (
    <div className="my-3 rounded border bg-card">
      <div className="max-h-80 overflow-auto">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-muted/50 backdrop-blur">
            <tr>
              {columns.map((c) => (
                <th key={c} className="px-3 py-2 text-left font-medium">
                  {c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.slice(0, 1000).map((row, i) => (
              <tr key={i} className="border-t hover:bg-muted/30">
                {columns.map((c) => (
                  <td key={c} className="px-3 py-1.5 font-mono">
                    {formatCell(row[c])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between border-t bg-muted/30 px-3 py-1.5 text-xs text-muted-foreground">
        <span>
          {rows.length.toLocaleString()} row{rows.length === 1 ? '' : 's'}
          {rows.length > 1000 ? ' (showing first 1,000)' : ''}
          {truncated ? ' · result was truncated' : ''}
        </span>
        {onSaveAsReport && (
          <Button variant="ghost" size="sm" onClick={onSaveAsReport}>
            <Save className="mr-1 h-3 w-3" /> Save as report
          </Button>
        )}
      </div>
    </div>
  )
}

function formatCell(v: unknown): string {
  if (v === null || v === undefined) return '—'
  if (typeof v === 'number') return v.toLocaleString()
  if (typeof v === 'object') return JSON.stringify(v)
  return String(v)
}

// ---------------------------------------------------------------------------
// ChartWidget
// ---------------------------------------------------------------------------

interface ChartWidgetProps {
  spec: ChartSpec
  rows: Record<string, unknown>[]
  onSaveAsReport?: () => void
}

const PALETTE = [
  '#3B82F6', // blue
  '#10B981', // emerald
  '#F59E0B', // amber
  '#8B5CF6', // violet
  '#EF4444', // red
  '#06B6D4', // cyan
  '#F97316', // orange
  '#84CC16', // lime
]

export function ChartWidget({ spec, rows, onSaveAsReport }: ChartWidgetProps) {
  const { chart_type, x, y, group, stacked, title } = spec

  // If grouping is requested, pivot rows so each group becomes a series.
  const { data, seriesKeys } = useMemo(() => {
    if (!group) {
      return { data: rows, seriesKeys: [y] }
    }
    const byX = new Map<string | number, Record<string, unknown>>()
    const groups = new Set<string>()
    for (const r of rows) {
      const xv = r[x] as string | number
      const gv = String(r[group])
      groups.add(gv)
      const bucket = byX.get(xv) ?? { [x]: xv }
      bucket[gv] = r[y]
      byX.set(xv, bucket)
    }
    return {
      data: Array.from(byX.values()),
      seriesKeys: Array.from(groups),
    }
  }, [rows, x, y, group])

  if (!rows.length) {
    return <div className="text-xs text-muted-foreground">No data to plot.</div>
  }

  const renderChart = () => {
    switch (chart_type) {
      case 'line':
        return (
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey={x} fontSize={11} />
            <YAxis fontSize={11} />
            <Tooltip />
            <Legend />
            {seriesKeys.map((k, i) => (
              <Line
                key={k}
                type="monotone"
                dataKey={k}
                stroke={PALETTE[i % PALETTE.length]}
                strokeWidth={2}
                dot={false}
              />
            ))}
          </LineChart>
        )
      case 'area':
        return (
          <AreaChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey={x} fontSize={11} />
            <YAxis fontSize={11} />
            <Tooltip />
            <Legend />
            {seriesKeys.map((k, i) => (
              <Area
                key={k}
                type="monotone"
                dataKey={k}
                stroke={PALETTE[i % PALETTE.length]}
                fill={PALETTE[i % PALETTE.length]}
                fillOpacity={0.3}
                stackId={stacked ? '1' : undefined}
              />
            ))}
          </AreaChart>
        )
      case 'pie': {
        // Pie uses {name, value}; flatten the first non-x column.
        const valueKey = seriesKeys[0]
        const pieData = data.map((d) => ({
          name: String(d[x]),
          value: Number(d[valueKey] ?? 0),
        }))
        return (
          <PieChart>
            <Pie data={pieData} dataKey="value" nameKey="name" outerRadius={90}>
              {pieData.map((_, i) => (
                <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
              ))}
            </Pie>
            <Tooltip />
            <Legend />
          </PieChart>
        )
      }
      case 'bar':
      default:
        return (
          <BarChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey={x} fontSize={11} />
            <YAxis fontSize={11} />
            <Tooltip />
            <Legend />
            {seriesKeys.map((k, i) => (
              <Bar
                key={k}
                dataKey={k}
                fill={PALETTE[i % PALETTE.length]}
                stackId={stacked ? '1' : undefined}
              />
            ))}
          </BarChart>
        )
    }
  }

  return (
    <div className="my-3 rounded border bg-card p-3">
      {title && (
        <div className="mb-2 text-sm font-medium text-foreground">{title}</div>
      )}
      <div className="h-72">
        <ResponsiveContainer width="100%" height="100%">
          {renderChart()}
        </ResponsiveContainer>
      </div>
      {onSaveAsReport && (
        <div className="mt-2 flex justify-end">
          <Button variant="ghost" size="sm" onClick={onSaveAsReport}>
            <Save className="mr-1 h-3 w-3" /> Save as report
          </Button>
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// MessageRenderer
// ---------------------------------------------------------------------------

interface MessageRendererProps {
  message: AnalysisMessage
}

const CHART_FENCE_RE = /```glaium-chart\s*\n([\s\S]*?)\n```/g

export function MessageRenderer({ message }: MessageRendererProps) {
  const segments = message.content || []
  const [showSteps, setShowSteps] = useState(false)

  // Index tool_result rows by id (so chart specs / inline tables can reference them).
  // Track call order so we can fall back to "the latest non-empty result" when a
  // chart spec's result_id doesn't match (LLM can't see real tool_use_ids until
  // after the call, so a self-emitted result_id is occasionally wrong).
  const { resultsById, resultsInOrder } = useMemo(() => {
    const byId: Record<string, { rows: Record<string, unknown>[]; columns: string[] }> = {}
    const ordered: Array<{ rows: Record<string, unknown>[]; columns: string[] }> = []
    for (const s of segments) {
      if (s.type === 'tool_result') {
        byId[s.id] = { rows: s.rows, columns: s.columns }
        ordered.push({ rows: s.rows, columns: s.columns })
      }
    }
    return { resultsById: byId, resultsInOrder: ordered }
  }, [segments])

  if (message.role === 'user') {
    const text = (segments[0] as { type: 'text'; text: string } | undefined)?.text ?? ''
    return (
      <div className="ml-auto max-w-3xl rounded-2xl bg-primary/10 px-4 py-2 text-sm">
        {text}
      </div>
    )
  }

  // Counts for the steps toggle.
  const toolCalls = segments.filter((s) => s.type === 'tool_call').length
  const toolResults = segments.filter((s) => s.type === 'tool_result')
  const errorResults = toolResults.filter((s) => s.type === 'tool_result' && s.error).length

  return (
    <div className="max-w-3xl space-y-1">
      {segments.map((seg, i) => {
        // Markdown is always shown — that's the answer.
        if (seg.type === 'markdown' || seg.type === 'text') {
          return (
            <MarkdownWithCharts
              key={i}
              text={seg.text}
              resultsById={resultsById}
              resultsInOrder={resultsInOrder}
            />
          )
        }
        // Charts always shown inline — they're the visualisation.
        if (seg.type === 'chart_spec') {
          const result =
            resultsById[seg.result_id] ?? findFallbackResult(resultsInOrder)
          if (!result || !result.rows.length) return null
          return <ChartWidget key={i} spec={seg.spec} rows={result.rows} />
        }
        // Tool calls + results: hidden by default. The model uses these as
        // working memory; the user just wants the conclusion.
        // Only surfaced when (a) the user opens "Show analysis steps", or
        // (b) it's an error worth flagging immediately.
        if (seg.type === 'tool_call') {
          return showSteps ? (
            <details
              key={i}
              className="my-1 rounded border bg-muted/30 px-2 py-1 text-xs"
              open
            >
              <summary className="cursor-pointer text-muted-foreground">
                Tool call: <code>{seg.name}</code>
              </summary>
              <pre className="mt-1 overflow-auto text-[11px]">
                {JSON.stringify(seg.args, null, 2)}
              </pre>
            </details>
          ) : null
        }
        if (seg.type === 'tool_result') {
          if (seg.error) {
            // Surface the error inline regardless — the user should see it.
            return (
              <div
                key={i}
                className="my-2 flex items-start gap-2 rounded border border-destructive/30 bg-destructive/5 p-2 text-xs text-destructive"
              >
                <AlertCircle className="mt-0.5 h-3 w-3" />
                Tool error{showSteps ? '' : ' (open analysis steps for details)'}
              </div>
            )
          }
          return showSteps ? (
            <TableWidget
              key={i}
              rows={seg.rows}
              columns={seg.columns}
              truncated={seg.truncated}
            />
          ) : null
        }
        return null
      })}

      {/* Steps toggle — only when there's something to reveal. */}
      {toolCalls > 0 && (
        <button
          type="button"
          onClick={() => setShowSteps((v) => !v)}
          className="mt-1 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          {showSteps ? (
            <ChevronDown className="h-3 w-3" />
          ) : (
            <ChevronRight className="h-3 w-3" />
          )}
          <Wrench className="h-3 w-3" />
          {showSteps ? 'Hide analysis steps' : `Show analysis steps (${toolCalls} ${toolCalls === 1 ? 'query' : 'queries'}${errorResults > 0 ? `, ${errorResults} error${errorResults === 1 ? '' : 's'}` : ''})`}
        </button>
      )}
    </div>
  )
}

function findFallbackResult(
  ordered: Array<{ rows: Record<string, unknown>[]; columns: string[] }>
): { rows: Record<string, unknown>[]; columns: string[] } | undefined {
  // Walk backwards: prefer the most recent non-empty result.
  for (let i = ordered.length - 1; i >= 0; i--) {
    if (ordered[i].rows.length > 0) return ordered[i]
  }
  return ordered[ordered.length - 1]
}

interface MarkdownWithChartsProps {
  text: string
  resultsById: Record<string, { rows: Record<string, unknown>[]; columns: string[] }>
  resultsInOrder: Array<{ rows: Record<string, unknown>[]; columns: string[] }>
}

/**
 * Render markdown but extract any ```glaium-chart {...}``` fenced blocks
 * and replace them with inline ChartWidget components. Tool results are
 * referenced by the spec's `result_id`. If the LLM emits a result_id that
 * doesn't match (it can't always see the real id), fall back to the most
 * recent non-empty tool_result in the message.
 */
function MarkdownWithCharts({ text, resultsById, resultsInOrder }: MarkdownWithChartsProps) {
  const parts = useMemo(() => {
    const out: Array<{ kind: 'md'; text: string } | { kind: 'chart'; spec: ChartSpec }> = []
    let lastIndex = 0
    const re = new RegExp(CHART_FENCE_RE.source, 'g')
    let m: RegExpExecArray | null
    while ((m = re.exec(text)) !== null) {
      if (m.index > lastIndex) {
        out.push({ kind: 'md', text: text.slice(lastIndex, m.index) })
      }
      try {
        const spec = JSON.parse(m[1]) as ChartSpec
        out.push({ kind: 'chart', spec })
      } catch {
        // If the JSON is malformed (e.g. partial during streaming), keep the
        // raw fenced block in markdown.
        out.push({ kind: 'md', text: m[0] })
      }
      lastIndex = re.lastIndex
    }
    if (lastIndex < text.length) {
      out.push({ kind: 'md', text: text.slice(lastIndex) })
    }
    return out
  }, [text])

  return (
    <Fragment>
      {parts.map((p, i) =>
        p.kind === 'md' ? (
          <div
            key={i}
            className="prose prose-sm max-w-none dark:prose-invert [&_table]:block [&_table]:overflow-x-auto [&_table]:border [&_th]:px-3 [&_th]:py-1.5 [&_th]:text-left [&_th]:font-medium [&_th]:bg-muted/50 [&_td]:px-3 [&_td]:py-1 [&_td]:border-t"
          >
            <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>
              {p.text}
            </ReactMarkdown>
          </div>
        ) : (
          <ChartWidget
            key={i}
            spec={p.spec}
            rows={resolveChartRows(p.spec, resultsById, resultsInOrder)}
          />
        )
      )}
    </Fragment>
  )
}

function resolveChartRows(
  spec: ChartSpec,
  byId: Record<string, { rows: Record<string, unknown>[]; columns: string[] }>,
  ordered: Array<{ rows: Record<string, unknown>[]; columns: string[] }>
): Record<string, unknown>[] {
  if (spec.result_id && byId[spec.result_id]?.rows.length) {
    return byId[spec.result_id].rows
  }
  // Fallback: latest non-empty tool result. Charts often follow the final
  // query, and the LLM can't always quote the right tool_use_id.
  for (let i = ordered.length - 1; i >= 0; i--) {
    if (ordered[i].rows.length > 0) return ordered[i].rows
  }
  return []
}
